// v10/src/components/EmergencyTrainer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Clock, Eye, Check, XCircle } from 'lucide-react';
import { emergencyService } from '../services/apiService';
import { speakElevenLabsOnly, stopSpeech } from '../utils/speechHelper';
import EmergencyResults from './EmergencyResults';

const REVEAL_SECONDS = 5;

const speak = (text, onEnd = () => {}) => speakElevenLabsOnly(text, onEnd);
const speakAndWait = (text) => new Promise(resolve => speak(text, resolve));

const EmergencyTrainer = ({ aircraft, procedureId, currentUser, darkMode, orgFeatures, onClose }) => {
  const [phase, setPhase]             = useState('loading');
  const [sessionData, setSessionData] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const [progress, setProgress]       = useState({ done: 0, total: 0 });
  const [elapsed, setElapsed]         = useState(0);
  const [results, setResults]         = useState(null);
  const [countdown, setCountdown]     = useState(3);
  const [cockpitUrl, setCockpitUrl]   = useState(null);
  const [selfErrors, setSelfErrors]   = useState(0);
  const [retryKey, setRetryKey]       = useState(0);
  const [revealCountdown, setRevealCountdown] = useState(REVEAL_SECONDS);

  const timerRef        = useRef(null);
  const startTimeRef    = useRef(null);
  const itemStartTimeRef = useRef(null);
  const sessionIdRef    = useRef(null);
  const touchStartXRef  = useRef(0);
  const revealTimerRef  = useRef(null);
  const timerPausedRef  = useRef(false);
  const pausedAtRef     = useRef(null);
  // ✅ Fix #4 — ref para evitar stale closure en completeSession
  const selfErrorsRef   = useRef(0);

  // ── Start session ──────────────────────────────────────────────
  useEffect(() => {
    setPhase('loading');
    setSessionData(null);
    setCurrentItem(null);
    setProgress({ done: 0, total: 0 });
    setElapsed(0);
    setResults(null);
    setCountdown(3);
    setSelfErrors(0);
    selfErrorsRef.current = 0; // ✅ Fix #4 — reset ref también
    setRevealCountdown(REVEAL_SECONDS);
    clearInterval(timerRef.current);
    clearInterval(revealTimerRef.current);

    const init = async () => {
      try {
        const res  = await emergencyService.startSession({ procedure_id: procedureId });
        const data = res.data?.data;
        if (!data?.session_id) throw new Error('No session_id');
        sessionIdRef.current = data.session_id;
        setSessionData(data);
        // ✅ Fix #1 — solo guardar la URL, NO mostrar toast al iniciar sesión.
        // El toast "imagen de cockpit subida" es para notificar al piloto cuando
        // el instructor sube una imagen EN VIVO (vía evento condor360:cockpit-updated).
        // Al arrancar un simulador nuevo la imagen ya existe — no es una novedad.
        setCockpitUrl(data.cockpit_image_url || null);
        setPhase('countdown');
      } catch (err) {
        console.error('[EmergencyTrainer] start error:', err);
      }
    };
    init();
    return () => { stopSpeech(); clearInterval(timerRef.current); clearInterval(revealTimerRef.current); };
  // eslint-disable-next-line
  }, [procedureId, retryKey]);

  // ── Escuchar actualizaciones de imagen en tiempo real ──────────
  // El toast "imagen de cockpit subida" se muestra SOLO cuando el instructor
  // sube una imagen nueva mientras el simulador ya está corriendo.
  const [cockpitSuccess, setCockpitSuccess] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      const newUrl = e.detail?.url;
      if (newUrl && newUrl !== cockpitUrl) {
        setCockpitUrl(newUrl);
        setCockpitSuccess(true);
        setTimeout(() => setCockpitSuccess(false), 3000);
      }
    };
    window.addEventListener('condor360:cockpit-updated', handler);
    return () => window.removeEventListener('condor360:cockpit-updated', handler);
  }, [cockpitUrl]);

  // ── Initial countdown (3-2-1) ──────────────────────────────────
  useEffect(() => {
    if (phase !== 'countdown') return;
    if (countdown <= 0) { startRunning(); return; }
    if (countdown === 3 && sessionData) {
      const title = sessionData?.title || 'procedimiento de emergencia';
      speak(`Iniciando simulador. ${title}.`);
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  // eslint-disable-next-line
  }, [phase, countdown, sessionData]);

  const startRunning = useCallback(async () => {
    const bench = sessionData?.benchmark_seconds || 0;
    await speakAndWait(`Tenés ${bench} segundos. Comenzá.`);
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 250);
    await fetchNextItem();
  // eslint-disable-next-line
  }, [sessionData]);

  // ── Reveal countdown ───────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'hidden') { clearInterval(revealTimerRef.current); return; }
    setRevealCountdown(REVEAL_SECONDS);
    revealTimerRef.current = setInterval(() => {
      setRevealCountdown(prev => {
        if (prev <= 1) { clearInterval(revealTimerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(revealTimerRef.current);
  }, [phase, currentItem?.id]); // eslint-disable-line

  useEffect(() => {
    if (phase === 'hidden' && revealCountdown === 0) doReveal();
  // eslint-disable-next-line
  }, [revealCountdown, phase]);

  // ── Fetch next item ────────────────────────────────────────────
  const fetchNextItem = async () => {
    try {
      const res  = await emergencyService.getNextItem(sessionIdRef.current);
      const data = res.data?.data;
      if (data?.completed) { await completeSession(); return; }
      setCurrentItem(data.item);
      setProgress(data.progress);
      itemStartTimeRef.current = Date.now();
      const stepNum = data.progress.done + 1;
      speak(`Paso ${stepNum}.`);
      setPhase('hidden');
    } catch (err) {
      console.error('[EmergencyTrainer] fetchNextItem error:', err);
    }
  };

  // ── Reveal ─────────────────────────────────────────────────────
  const doReveal = () => {
    if (phase !== 'hidden') return;
    clearInterval(revealTimerRef.current);
    try { navigator.vibrate(40); } catch (_) {}
    setPhase('revealed');
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerPausedRef.current = true;
      pausedAtRef.current    = Date.now();
    }
    if (currentItem) {
      const fullText = `${currentItem.text}${currentItem.detail ? '. ' + currentItem.detail : ''}`;
      speak(fullText, () => {
        if (timerPausedRef.current && startTimeRef.current) {
          const pausedMs = Date.now() - (pausedAtRef.current || Date.now());
          startTimeRef.current += pausedMs;
          timerRef.current = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
          }, 250);
          timerPausedRef.current = false;
          pausedAtRef.current    = null;
        }
      });
    }
  };

  const handleReveal = () => { if (phase !== 'hidden') return; doReveal(); };

  // ── Self-evaluation ────────────────────────────────────────────
  const handleSelfEval = async (correct) => {
    if (phase !== 'revealed' || !currentItem) return;
    const secondsTaken = Math.max(1, Math.floor((Date.now() - itemStartTimeRef.current) / 1000));
    setPhase('selfeval');

    if (!correct) {
      // ✅ Fix #4 — actualizar ref Y state para que completeSession lea el valor correcto
      selfErrorsRef.current += 1;
      setSelfErrors(selfErrorsRef.current);
      try { navigator.vibrate([200, 100, 200]); } catch (_) {}
    } else {
      try { navigator.vibrate(40); } catch (_) {}
    }

    try {
      const res  = await emergencyService.itemDone(sessionIdRef.current, {
        item_id:      currentItem.id,
        seconds_taken: secondsTaken,
      });
      const data = res.data?.data;

      if (data?.is_order_error) {
        setPhase('error_flash');
        speak('Paso incorrecto. Revisá el procedimiento.');
        setTimeout(() => advanceToNext(data), 2000);
        return;
      }

      if (!correct) setTimeout(() => advanceToNext(data), 1500);
      else advanceToNext(data);
    } catch (err) {
      console.error('[EmergencyTrainer] itemDone error:', err);
    }
  };

  const advanceToNext = (data) => {
    if (data?.next_item) {
      setCurrentItem(data.next_item);
      setProgress(prev => {
        const next = prev.done + 1;
        speak(`Paso ${next + 1}.`);
        return { ...prev, done: next };
      });
      itemStartTimeRef.current = Date.now();
      setRevealCountdown(REVEAL_SECONDS);
      setPhase('hidden');
    } else {
      setProgress(prev => ({ ...prev, done: prev.done + 1 }));
      completeSession();
    }
  };

  // ── Complete session ───────────────────────────────────────────
  const completeSession = async () => {
    clearInterval(timerRef.current);
    clearInterval(revealTimerRef.current);
    try {
      const res  = await emergencyService.completeSession(sessionIdRef.current, {
        total_seconds: Math.floor((Date.now() - startTimeRef.current) / 1000),
      });
      const data = res.data?.data;
      // ✅ Fix #4 — usar selfErrorsRef.current en vez de selfErrors (evita stale closure)
      setResults({ ...data, self_errors: selfErrorsRef.current });
      setPhase('results');

      const score = data?.score ?? 0;
      const msg   = score >= 90 ? 'Procedimiento dominado. Tiempo excelente.'
                  : score >= 70 ? 'Aprobado. Practicá los pasos donde te equivocaste.'
                  : 'Necesitás más práctica. Revisá el procedimiento con tu instructor.';
      speak(msg);
    } catch (err) {
      console.error('[EmergencyTrainer] complete error:', err);
      setResults({ self_errors: selfErrorsRef.current });
      setPhase('results');
    }
  };

  // ── Swipe / keyboard ───────────────────────────────────────────
  const handleTouchStart = (e) => { touchStartXRef.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    const diff = e.changedTouches[0].clientX - touchStartXRef.current;
    if (diff > 60 && phase === 'hidden') handleReveal();
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (phase === 'hidden'   && (e.key === 'Enter' || e.key === ' ')) handleReveal();
      if (phase === 'revealed' && e.key === 'ArrowRight') handleSelfEval(true);
      if (phase === 'revealed' && e.key === 'ArrowLeft')  handleSelfEval(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // ── Timer color ────────────────────────────────────────────────
  const benchmark  = sessionData?.benchmark_seconds || 999;
  const timerColor = elapsed > benchmark ? '#ef4444' : elapsed > benchmark * 0.8 ? '#f59e0b' : '#22c55e';
  const timerPulse = elapsed > benchmark;
  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const handleRetry = () => { stopSpeech(); setRetryKey(k => k + 1); };

  if (phase === 'results') {
    return <EmergencyResults results={results} onRetry={handleRetry} onClose={onClose} darkMode={darkMode} />;
  }

  const bgStyle = cockpitUrl
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.72),rgba(0,0,0,0.72)),url(${cockpitUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: 'linear-gradient(135deg,#0a0f1e 0%,#111827 50%,#0f172a 100%)' };

  const isActive = phase === 'hidden' || phase === 'revealed' || phase === 'selfeval' || phase === 'error_flash';

  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', flexDirection:'column', ...bgStyle }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ✅ Fix #1 — Toast solo aparece ante evento condor360:cockpit-updated */}
      <AnimatePresence>
        {cockpitSuccess && (
          <motion.div
            initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-20 }}
            style={{ position:'absolute', top:16, left:'50%', transform:'translateX(-50%)', zIndex:100,
              background:'rgba(34,197,94,0.9)', backdropFilter:'blur(8px)',
              padding:'10px 20px', borderRadius:20,
              display:'flex', alignItems:'center', gap:8, whiteSpace:'nowrap' }}>
            <span style={{ fontSize:16 }}>✅</span>
            <span style={{ fontSize:13, fontWeight:700, color:'white' }}>Imagen de cockpit actualizada</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div style={{ padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontSize:16, fontWeight:800, color:'white', lineHeight:1.3 }}>
            {sessionData?.title || 'Cargando...'}
          </div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)', marginTop:2 }}>
            {aircraft?.manufacturer} {aircraft?.model_name} · {aircraft?.registration}
          </div>
        </div>
        <button onClick={onClose}
          style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:12, padding:8, cursor:'pointer' }}>
          <X size={20} color="rgba(255,255,255,0.7)" />
        </button>
      </div>

      {/* Loading */}
      <AnimatePresence>
        {phase === 'loading' && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16 }}>
            <motion.div animate={{ scale:[1,1.2,1] }} transition={{ duration:1.5, repeat:Infinity }}>
              <span style={{ fontSize:64 }}>🚨</span>
            </motion.div>
            <span style={{ fontSize:15, fontWeight:600, color:'rgba(255,255,255,0.6)' }}>Preparando simulador...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Initial countdown */}
      <AnimatePresence>
        {phase === 'countdown' && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column' }}>
            <motion.div key={countdown}
              initial={{ scale:0.3, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:2, opacity:0 }}
              transition={{ duration:0.4 }}
              style={{ fontSize:120, fontWeight:900, color:'white', lineHeight:1 }}>
              {countdown || 'GO'}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active phases */}
      {isActive && (
        <>
          {/* Timer */}
          <div style={{ textAlign:'center', padding:'12px 0' }}>
            <motion.div
              animate={timerPulse ? { scale:[1,1.05,1] } : {}}
              transition={timerPulse ? { duration:0.8, repeat:Infinity } : {}}
              style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'8px 20px', borderRadius:16, background:'rgba(0,0,0,0.4)' }}>
              <Clock size={18} color={timerColor} />
              <span style={{ fontSize:32, fontWeight:900, color:timerColor, fontVariantNumeric:'tabular-nums' }}>
                {formatTime(elapsed)}
              </span>
            </motion.div>
          </div>

          {/* Progress bar */}
          <div style={{ padding:'0 24px', display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ flex:1, height:6, borderRadius:3, background:'rgba(255,255,255,0.1)', overflow:'hidden' }}>
              <motion.div
                animate={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
                transition={{ type:'spring', stiffness:120, damping:20 }}
                style={{ height:'100%', borderRadius:3, background:'linear-gradient(90deg,#22c55e,#16a34a)' }}
              />
            </div>
            {/* ✅ Fix #2 — mostrar paso actual (1-indexed) en vez de pasos completados */}
            <span style={{ fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.5)', whiteSpace:'nowrap' }}>
              {Math.min(progress.done + 1, progress.total)}/{progress.total}
            </span>
          </div>

          {/* Current item area */}
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px 32px' }}>
            <AnimatePresence mode="wait">
              {phase === 'error_flash' ? (
                <motion.div key="error"
                  initial={{ scale:0.8, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.8, opacity:0 }}
                  style={{ textAlign:'center' }}>
                  <AlertTriangle size={64} color="#ef4444" style={{ marginBottom:16 }} />
                  <div style={{ fontSize:22, fontWeight:800, color:'#ef4444', marginBottom:8 }}>Error de orden</div>
                  <div style={{ fontSize:15, color:'rgba(255,255,255,0.6)' }}>Este paso crítico no era el siguiente. Continuá...</div>
                </motion.div>

              ) : phase === 'hidden' && currentItem ? (
                <motion.div key={`hidden-${currentItem.id}`}
                  initial={{ x:60, opacity:0 }} animate={{ x:0, opacity:1 }} exit={{ x:-60, opacity:0 }}
                  transition={{ type:'spring', stiffness:200, damping:25 }}
                  style={{ textAlign:'center', width:'100%' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:16 }}>
                    Paso {progress.done + 1}
                  </div>
                  <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', padding:'32px 48px', borderRadius:20, minWidth:200, minHeight:100, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                    <motion.span
                      key={revealCountdown}
                      initial={{ scale:0.5, opacity:0 }}
                      animate={{ scale:1, opacity:1 }}
                      transition={{ type:'spring', stiffness:300, damping:20 }}
                      style={{ fontSize:72, fontWeight:900, lineHeight:1, color: revealCountdown <= 2 ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.2)', fontVariantNumeric:'tabular-nums' }}>
                      {revealCountdown}
                    </motion.span>
                  </div>
                </motion.div>

              ) : (phase === 'revealed' || phase === 'selfeval') && currentItem ? (
                <motion.div key={`revealed-${currentItem.id}`}
                  initial={{ scale:0.9, opacity:0 }} animate={{ scale:1, opacity:1 }}
                  transition={{ type:'spring', stiffness:200, damping:25 }}
                  style={{ textAlign:'center', width:'100%' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:16 }}>
                    Paso {progress.done + 1}
                  </div>
                  <motion.div initial={{ y:10, opacity:0 }} animate={{ y:0, opacity:1 }} transition={{ delay:0.1 }}>
                    <div style={{ fontSize:28, fontWeight:900, color:'white', lineHeight:1.2, marginBottom:8 }}>
                      {currentItem.text}
                    </div>
                    {currentItem.detail && (
                      <div style={{ fontSize:18, fontWeight:600, color:'rgba(255,255,255,0.55)', marginTop:4 }}>
                        ▼ {currentItem.detail}
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          {/* Action buttons */}
          <div style={{ padding:'0 24px 32px', paddingBottom:'max(32px, env(safe-area-inset-bottom))' }}>
            <AnimatePresence mode="wait">
              {phase === 'hidden' && (
                <motion.div key="reveal-btn" initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }}>
                  <motion.button
                    onClick={handleReveal}
                    whileHover={{ scale:1.02 }} whileTap={{ scale:0.96 }}
                    style={{ width:'100%', padding:'20px', border:'none', borderRadius:20, cursor:'pointer', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'white', fontWeight:800, fontSize:18, fontFamily:'inherit', boxShadow:'0 8px 28px rgba(99,102,241,0.35)', display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                    <Eye size={22} />
                    Revelar paso
                  </motion.button>
                  <div style={{ textAlign:'center', marginTop:8 }}>
                    <span style={{ fontSize:11, color:'rgba(255,255,255,0.25)' }}>
                      Swipe derecho o Enter · Se revela en {revealCountdown}s
                    </span>
                  </div>
                </motion.div>
              )}

              {phase === 'revealed' && (
                <motion.div key="eval-btns" initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.4)', textAlign:'center', marginBottom:12 }}>
                    ¿Lo dijiste correctamente?
                  </div>
                  <div style={{ display:'flex', gap:12 }}>
                    <motion.button
                      onClick={() => handleSelfEval(true)}
                      whileHover={{ scale:1.02 }} whileTap={{ scale:0.96 }}
                      style={{ flex:1, padding:'18px', border:'none', borderRadius:18, cursor:'pointer', background:'linear-gradient(135deg,#22c55e,#16a34a)', color:'white', fontWeight:800, fontSize:16, fontFamily:'inherit', boxShadow:'0 6px 20px rgba(34,197,94,0.35)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                      <Check size={20} />
                      Correcto
                    </motion.button>
                    <motion.button
                      onClick={() => handleSelfEval(false)}
                      whileHover={{ scale:1.02 }} whileTap={{ scale:0.96 }}
                      style={{ flex:1, padding:'18px', border:'none', borderRadius:18, cursor:'pointer', background:'linear-gradient(135deg,#ef4444,#dc2626)', color:'white', fontWeight:800, fontSize:16, fontFamily:'inherit', boxShadow:'0 6px 20px rgba(239,68,68,0.35)', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                      <XCircle size={20} />
                      Me equivoqué
                    </motion.button>
                  </div>
                  <div style={{ textAlign:'center', marginTop:8 }}>
                    <span style={{ fontSize:11, color:'rgba(255,255,255,0.25)' }}>
                      → Correcto · ← Me equivoqué
                    </span>
                  </div>
                </motion.div>
              )}

              {(phase === 'selfeval' || phase === 'error_flash') && (
                <motion.div key="waiting" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                  style={{ textAlign:'center', padding:'20px 0' }}>
                  <div style={{ width:24, height:24, border:'3px solid rgba(255,255,255,0.2)', borderTopColor:'white', borderRadius:'50%', margin:'0 auto', animation:'spin 0.8s linear infinite' }} />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </>
      )}
    </div>
  );
};

export default EmergencyTrainer;
